import { lazy, Suspense, useDeferredValue, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { BOOK_STATUSES, type Book, type BookStatus } from '../../shared/api';
import { BookCover } from '../components/BookCover';
import { CategoryChips } from '../components/CategoryChips';
import { StatusBadge } from '../components/StatusBadge';
import { useCategories } from '../hooks/useCategories';
import { useResource } from '../hooks/useResource';
import { useStoredState } from '../hooks/useStoredState';
import { api, errorMessage, EXPORT_URLS } from '../lib/api';
import { formatAuthors, STATUS_LABELS } from '../lib/format';
import { matchesQuery } from '../lib/search';
import { readStorage, writeStorage } from '../lib/storage';

const CATEGORY_STORAGE_KEY = 'my-catalogue:books-category';

const ScanFinder = lazy(() => import('../components/ScanFinder'));

type StatusFilter = 'all' | BookStatus;
/** "all", "none" (no category), or a category id. */
type CategoryFilter = string;

function inCategory(book: Book, filter: CategoryFilter): boolean {
  if (filter === 'all') return true;
  if (filter === 'none') return book.categories.length === 0;
  return book.categories.some((category) => String(category.id) === filter);
}

export function CollectionPage() {
  const { data: books, error, loading, reload } = useResource('books', api.listBooks, {
    // Fast-mode scans are enriched in the background; keep the list fresh until they're done.
    pollWhile: (list) => list.some((book) => book.status === 'pending'),
  });
  const { categories } = useCategories();
  const [query, setQuery] = useState('');
  // Filters survive opening a book and coming back (and the next visit).
  const [statusFilter, setStatusFilter] = useStoredState<StatusFilter>('my-catalogue:books-status', 'all', ['all', ...BOOK_STATUSES]);
  // The category is kept in the URL so it can be linked to (e.g. from a book or the categories page),
  // and remembered for when the page is opened without one.
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryFilter: CategoryFilter = searchParams.get('category') ?? readStorage(CATEGORY_STORAGE_KEY) ?? 'all';
  const deferredQuery = useDeferredValue(query.trim());
  const [finding, setFinding] = useState(false);
  const navigate = useNavigate();

  /** An ISBN read in the list: open the book, or offer to add it. */
  function findByIsbn(isbn: string) {
    navigate((books ?? []).some((book) => book.isbn === isbn) ? `/books/${isbn}` : `/scan?scan=books&isbn=${isbn}`);
  }

  function setCategoryFilter(value: CategoryFilter) {
    writeStorage(CATEGORY_STORAGE_KEY, value);
    const next = new URLSearchParams(searchParams);
    if (value === 'all') next.delete('category');
    else next.set('category', value);
    setSearchParams(next, { replace: true });
  }

  const categoryCounts = useMemo(() => {
    const counts = new Map<string, number>([['none', 0]]);
    for (const book of books ?? []) {
      if (book.categories.length === 0) counts.set('none', (counts.get('none') ?? 0) + 1);
      for (const category of book.categories) counts.set(String(category.id), (counts.get(String(category.id)) ?? 0) + 1);
    }
    return counts;
  }, [books]);

  const counts = useMemo(() => {
    const result: Record<BookStatus, number> = { pending: 0, complete: 0, not_found: 0, error: 0 };
    for (const book of books ?? []) result[book.status] += 1;
    return result;
  }, [books]);

  const visible = useMemo(
    () =>
      (books ?? []).filter(
        (book) =>
          (statusFilter === 'all' || book.status === statusFilter) &&
          inCategory(book, categoryFilter) &&
          matchesQuery(book, deferredQuery),
      ),
    [books, statusFilter, categoryFilter, deferredQuery],
  );

  if (books === null) {
    if (loading) return <p className="page-status">Loading collection…</p>;
    return (
      <div className="page-status" role="alert">
        <p className="error-text">{errorMessage(error)}</p>
        <button type="button" className="btn btn--primary" onClick={reload}>
          Try again
        </button>
      </div>
    );
  }

  return (
    <section className="collection-page">
      <header className="page-header">
        <div>
          <h1>Collection</h1>
          <p className="muted">
            {books.length} {books.length === 1 ? 'book' : 'books'}
            {counts.pending > 0 && ` · ${counts.pending} fetching details`}
          </p>
        </div>
        <div className="export-buttons">
          <Link className="btn btn--ghost btn--small" to="/categories">
            Categories
          </Link>
          <a className="btn btn--secondary btn--small" href={EXPORT_URLS.json} download>
            Export JSON
          </a>
          <a className="btn btn--secondary btn--small" href={EXPORT_URLS.csv} download>
            Export CSV
          </a>
        </div>
      </header>

      {error !== null && (
        <p className="error-text" role="alert">
          Couldn't refresh: {errorMessage(error)}
        </p>
      )}

      {books.length === 0 ? (
        <div className="empty-state">
          <p>Your collection is empty.</p>
          <Link className="btn btn--primary" to="/scan?scan=books">
            Scan your first book
          </Link>
        </div>
      ) : (
        <>
          <div className="filters">
            <div className="filters__row">
              <input
                type="search"
                className="filters__search"
                placeholder="Search title, author or ISBN"
                aria-label="Search the collection"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              <button
                type="button"
                className={`btn btn--small filters__scan${finding ? ' btn--primary' : ' btn--secondary'}`}
                aria-pressed={finding}
                title="Find a book by scanning its barcode"
                onClick={() => setFinding((open) => !open)}
              >
                <span aria-hidden="true">📷</span> Find
              </button>
            </div>
            {finding && (
              <Suspense fallback={<p className="hint filters__wide">Loading camera…</p>}>
                <ScanFinder kind="books" onCode={findByIsbn} onClose={() => setFinding(false)} />
              </Suspense>
            )}
            <select
              className="filters__status"
              aria-label="Filter by category"
              value={categoryFilter}
              onChange={(event) => setCategoryFilter(event.target.value)}
            >
              <option value="all">All categories</option>
              {(categories ?? []).map((category) => (
                <option key={category.id} value={String(category.id)}>
                  {category.name} ({categoryCounts.get(String(category.id)) ?? 0})
                </option>
              ))}
              <option value="none">No category ({categoryCounts.get('none') ?? 0})</option>
            </select>
            <select
              className="filters__status"
              aria-label="Filter by status"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value as StatusFilter)}
            >
              <option value="all">All statuses</option>
              {BOOK_STATUSES.map((status) => (
                <option key={status} value={status} disabled={counts[status] === 0}>
                  {STATUS_LABELS[status]} ({counts[status]})
                </option>
              ))}
            </select>
          </div>

          {visible.length === 0 ? (
            <p className="hint">
              No books match.{' '}
              <Link to="/categories" className="muted">
                Manage categories
              </Link>
            </p>
          ) : (
            <ul className="book-grid">
              {visible.map((book) => (
                <li key={book.isbn}>
                  <Link className="book-tile" to={`/books/${book.isbn}`}>
                    <BookCover src={book.coverUrl} title={book.title} size="small" />
                    <div className="book-tile__body">
                      <h2 className="book-tile__title">{book.title ?? `ISBN ${book.isbn}`}</h2>
                      {book.status === 'complete' && <p className="book-tile__authors">{formatAuthors(book.authors)}</p>}
                      {book.status !== 'complete' && <StatusBadge status={book.status} />}
                      <CategoryChips categories={book.categories} />
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}
